import { Icon } from "@iconify/react";
import { Pagination } from "@material-ui/lab";
import Breadcrumbs from "components/Breadcrumbs";
import Link from "components/Link";
import NoDataAvailable from "components/NoDataAvailable";
import SEO from "components/SEO";
import SessionContext from "components/session/SessionContext";
import Text from "components/Text";
import { pagePadding } from "data/commonClasses";
import {
  dateFormatter,
  fetchFromAPI,
  getTranslationContent,
  isArray,
} from "functions";
import Image from "next/image";
import { useContext, useEffect, useState } from "react";
import useStyles from "styles/Trainings.Style";
import { twMerge } from "tailwind-merge";
import useTranslation from "translations/hooks/useTranslation";

export default function TrainingCenter({
  initialTrainings,
  initialTotalCount,
}) {
  const { t } = useTranslation();
  let {
    session: { lang },
  } = useContext(SessionContext);
  let isAr = lang === "ar";
  const classes = useStyles();
  
  const [selectedFilter, setSelectedFilter] = useState("current");
  const [trainings, setTrainings] = useState(initialTrainings);
  const [currentPage, setCurrentPage] = useState(1);

  const [totalPages, setTotalPages] = useState(
    Math.ceil(initialTotalCount / 8)
  );

  useEffect(() => {
    fetchTrainings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter, currentPage, lang]);

  const fetchTrainings = async () => {
    const reqBody = {
      limit: 8,
      page: currentPage,
      needCount: true,
      // title,
      lang,
      ...(selectedFilter === "future" && { upcomingOnly: true }),
      ...(selectedFilter === "current" && { currentEvents: true }),
      ...(selectedFilter === "past" && { pastOnly: true }),
    };
    // console.log({selectedFilter,currentPage});
    try {
      const response = await fetchFromAPI({ api: "trainings", reqBody });
      const fetchedTrainings = response?.response?.data;
      const totalCount = response?.response?.rowsCount?.[0]?.rowsCount || 0;
      // console.log({ fetchedTrainings, totalCount });
      setTrainings(fetchedTrainings);
      setTotalPages(Math.ceil(totalCount / 8));
    } catch (error) {
      console.error("Error fetching trainings:", error);
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };


  let breadcrumbs = [
    { text: t("home"), href: "/" },
    { text: t("trainingCenter") },
    // { text: t(section) },
  ];

  return (
    <div className={twMerge("flex flex-col w-full mb-10", pagePadding)}>
      <SEO
        title={t("trainingCenter")}
        description={t("trainingsDescription")}
      />
      <Breadcrumbs items={breadcrumbs} />
      <Text text={t("trainingCenter")} sectionTitle bold className="my-5" />
     
      <div className={"flex w-full bg-lightGrey rounded-button mb-4"}>
        {["current", "future", "past"].map((tab) => (
          <div
            key={tab}
            onClick={() => {
              setCurrentPage(1);
              setSelectedFilter(tab);
            }}
            className={`w-1/3 p-4 text-center cursor-pointer ${
              selectedFilter == tab.toLowerCase() &&
              `bg-orange text-white rounded-button`
            }`}
          >
            {t(`${tab}Trainings`)}
          </div>
        ))}
      </div>
      <div className={"bg-lightGrey p-5 rounded-button"}>

        {!isArray(trainings) ? (
          <NoDataAvailable title={"noTrainingsAvailable"} />
        ) : (
          <>
            <div className={"flex flex-row flex-wrap gap-5"}>
              {trainings.map((training) => (
                <Link
                  href="/trainings/details/[slug]"
                  as={`/trainings/details/${training.slug}`}
                  key={training._id}
                >
                  <div
                    className={`flex md:flex-row items-center justify-between rounded-button md:w-[49%] bg-white p-2 border border-[#E8E3CF] cursor-pointer 
  flex-col w-full`}
                  >
                    <div
                      className={`md:w-[40%] w-full min-w-[200px] block h-auto sm:w-auto`}
                    >
                      <Image
                        src={
                          training?.image 
                            ? `${training.image}`
                            : "/placeholder.jpg"
                        }
                        alt="training image"
                        layout="responsive"
                        height={120}
                        width={"100%"}
                      />
                    </div>
                    <div
                      className={`h-full w-full md:w-[60%] pl-2 pr-2 pt-5 pb-2 flex flex-col justify-between items-start sm:w-full`}
                    >
                      <Text
                        direction={"ltr"}
                        text={getTranslationContent(training, "title", lang)}
                        className={
                          "text-subtitle text-oeaMain font-bold w-full"
                        }
                      />
                      <div
                        className={
                          "flex flex-col w-full mt-5 md:mt-0 items-center justify-center md:justify-start md:items-start"
                        }
                      >
                        <div
                          className={
                            "flex flex-row items-center p-2 justify-around bg-oeaMain rounded-button"
                          }
                        >
                          <Icon
                            className={`${
                              isAr ? "ml-2" : "mr-2"
                            } w-6 h-6 flex text-input text-white`}
                            icon={"basil:calendar-solid"}
                          />
                          <div className={"text-input text-white font-bold"}>
                            {dateFormatter(
                              training.start,
                              "DDDD D MMMM YYYY",
                              isAr
                            )}
                          </div>
                        </div>
                        <div
                          className={`flex ${
                            isAr ? "flex-row-reverse" : "flex-row"
                          } gap-5 items-start  `}
                        >
                          <div className={"flex flex-row items-center mt-5"}>
                            <div className="text-input md:text-infoText mr-2 text-transparentBlack">
                              {t("shares")}
                            </div>
                            <Icon
                              icon={"material-symbols:share"}
                              className="mr-2 w-5 h-5 text-transparentBlack"
                            />
                          </div>
                          <div className={"flex flex-row items-center mt-5"}>
                            <div className="text-input md:text-infoText mr-2 text-transparentBlack">
                              {training?.interactionsSummary?.likes}{" "}
                              {t("likes")}
                            </div>
                            <Icon
                              icon={"solar:like-bold"}
                              className="mr-2 w-5 h-5 text-transparentBlack"
                            />
                          </div>
                          <div className={"flex flex-row items-center mt-5"}>
                            <div className="text-input md:text-infoText mr-2 text-transparentBlack">
                              {training?.interactionsSummary?.views}{" "}
                              {t("views")}
                            </div>
                            <Icon
                              icon={"carbon:view-filled"}
                              className="mr-2 w-5 h-5 text-transparentBlack"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            {totalPages > 1 && (
              <div className={classes.paginationWrapper}>
                <Pagination
                  page={currentPage}
                  count={totalPages}
                  siblingCount={0}
                  onChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export async function getServerSideProps({ query }) {
  let { lang } = query;

  const reqBody = {
    limit: 8,
    page: 1,
    needCount: true,
    lang,
    currentEvents: true,
  };

  try {
    const response = await fetchFromAPI({ api: "trainings", reqBody });
    const trainings = response?.response?.data || [];
    const trainingsCount = response?.response?.rowsCount?.[0]?.rowsCount || 0;
    return {
      props: { initialTrainings: trainings, initialTotalCount: trainingsCount },
    };
  } catch (error) {
    console.error("Error fetching initial trainings:", error);
    return { props: { initialTrainings: [], initialTotalCount: 0 } };
  }

}
